/**
 * Loop Player Mixin
 * 
 * Provides shared functionality for all loop player components:
 * - Loop management (CRUD, activate, select by index)
 * - Edit mode (enter, exit, adjust boundaries, switch boundary)
 * - Countdown before playback
 * - Keyboard shortcuts
 * - State persistence
 * - Drag handling for timeline
 * 
 * Components must implement abstract methods for media-specific operations.
 */

import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';
import { createDebouncedSave, loadState } from '../../services/index.js';
import { formatTime, formatTimeMs, generateId, getNextLoopColor } from '../../utils/index.js';
import {
    chevronDownIcon,
    chevronLeftIcon,
    chevronRightIcon,
    countdownIcon,
    listIcon,
    pauseIcon,
    playIcon,
    plusIcon,
    repeatIcon,
    seekBackwardIcon,
    seekForwardIcon,
    timerIcon,
    trashIcon,
    zoomIcon
} from './icons.js';
import type { EditState, Loop, PlayerState } from './types.js';
import { ADJUSTMENTS, TIMELINE, TIMING } from './types.js';

// Type for the constructor constraint - must be compatible with LitElement
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Constructor<T = object> = abstract new (...args: any[]) => T;

// Interface for what the mixin adds
export interface LoopPlayerMixinInterface {
    // Properties (must be @property() on the concrete class)
    storagePrefix: string;
    autoRestore: boolean;
    showShortcuts: boolean;

    // Abstract properties for compact view
    readonly mediaIcon: string;
    getCompactTitle(): string;
    getSubtitle(): string;  // Optional subtitle (e.g., channel name)

    // State
    expanded: boolean;
    currentTime: number;
    duration: number;
    isPlaying: boolean;
    playbackSpeed: number;
    loopEnabled: boolean;
    loops: Loop[];
    activeLoopId: string | null;
    editState: EditState;
    countdown: number | null;
    countdownEnabled: boolean;
    draggingBoundary: 'start' | 'end' | null;

    // Computed
    readonly activeLoop: Loop | null;
    readonly editingLoop: Loop | null;
    readonly isEditing: boolean;

    // Abstract methods (must be implemented by component)
    getMediaId(): string;
    play(): void;
    pause(): void;
    getCurrentTime(): number;
    getDuration(): number;
    seekTo(time: number): void;
    setPlaybackRate(rate: number): void;
    isMediaReady(): boolean;

    // Lifecycle hooks (optional)
    onFirstExpand?(): void;

    // Common methods
    toggleExpanded(): void;
    togglePlayPause(): void;
    seek(seconds: number): void;
    setSpeed(speed: number): void;
    adjustSpeed(delta: number): void;
    createLoop(start: number, end: number): Loop;
    updateLoop(loopId: string, updates: Partial<Loop>): void;
    deleteLoop(loopId: string): void;
    activateLoop(loopId: string | null): void;
    toggleLoopEnabled(): void;
    toggleCountdownEnabled(): void;
    selectLoopByIndex(index: number): void;
    enterEditMode(loopId: string, boundary: 'start' | 'end'): void;
    exitEditMode(): void;
    adjustEditingBoundary(delta: number): void;
    switchEditBoundary(): void;
    setBoundaryToCurrent(boundary: 'start' | 'end'): void;
    previewLoop(): void;
    previewEnd(): void;
    returnToLoopStart(): void;
    startCountdownThenPlay(startTime: number): void;
    cancelCountdown(): void;
    handleKeyboard(e: KeyboardEvent): void;
    startDrag(boundary: 'start' | 'end', loopId: string, e: MouseEvent | TouchEvent): void;
    getTimelineZoom(): { start: number; end: number; isZoomed: boolean };
    handleTimelineClick(e: MouseEvent): void;
    saveCurrentState(): void;
    restoreState(): void;
    startTimeUpdate(): void;
    stopTimeUpdate(): void;
    checkLoopBoundary(): void;
    _onMediaReady(): void;

    // Shared rendering methods
    _renderHeader(): TemplateResult;
    _renderControlsAndTimeline(): TemplateResult;
    _renderCompactView(): TemplateResult;
    _renderControls(): TemplateResult;
    _renderTimeline(editable: boolean): TemplateResult;
    _renderDragHandles(): TemplateResult | null;
    _renderEditPanel(): TemplateResult | null;
    _renderLoopList(): TemplateResult;
    _renderShortcuts(): TemplateResult | null;
    _handleTimelineClick(e: MouseEvent): void;
    _handleLoopClick(loopId: string): void;
    _handleLoopDblClick(loopId: string): void;
    _addNewLoop(): void;
    _renderDeleteDialog(): TemplateResult | null;
}

/**
 * Mixin that adds loop player functionality to a LitElement
 */
export function LoopPlayerMixin<T extends Constructor<LitElement>>(
    superClass: T
) {
    abstract class LoopPlayerMixinClass extends superClass implements LoopPlayerMixinInterface {
        // ─── State ─────────────────────────────────────────────────────────────

        /** Whether the player is expanded (showing full controls) or collapsed (compact view) */
        @property({ type: Boolean, reflect: true }) expanded = false;
        /** Whether loop mode is enabled - reflects to attribute for CSS */
        @property({ type: Boolean, reflect: true, attribute: 'loop-enabled' }) loopEnabled = false;
        @state() currentTime = 0;
        @state() duration = 0;
        @state() isPlaying = false;
        @state() playbackSpeed = 1;
        @state() loops: Loop[] = [];
        @state() activeLoopId: string | null = null;
        @state() editState: EditState = { loopId: null, boundary: null };
        @state() countdown: number | null = null;
        @state() countdownEnabled = true;
        @state() draggingBoundary: 'start' | 'end' | null = null;
        @state() private _hoverPreviewPercent: number | null = null;
        @state() zoomToLoop = false;  // Zoom timeline to show just the active loop
        private _zoomWasEnabled = false;  // Remember zoom state when loop mode is toggled off
        @state() private _editingLoopName = false;  // Whether loop name is being edited
        @state() private _editingStartTime = false;  // Whether start time is being edited
        @state() private _editingEndTime = false;    // Whether end time is being edited
        @state() private _pendingDeleteLoopId: string | null = null;
        @state() private _loopDropdownOpen = false;  // Loop selector dropdown state

        // Private state
        private _updateInterval: number | null = null;
        private _countdownTimer: number | null = null;
        private _pendingStartTime: number | null = null;
        private _pendingAutoPlay = false;  // Auto-play when media becomes ready
        private _debouncedSave: ((state: PlayerState) => void) | null = null;
        private _timelineRef: HTMLElement | null = null;
        private _lockedZoom: { start: number; end: number } | null = null;
        private _dragStartX: number | null = null;  // Initial click X position
        private _dragStartTime: number | null = null;  // Initial boundary time value
        private _longPressTimer: number | null = null;  // Long-press detection for mobile
        private _boundClickOutsideHandler = this._handleClickOutside.bind(this);
        private _boundDropdownCloseHandler = this._closeLoopDropdown.bind(this);

        // ─── Computed ──────────────────────────────────────────────────────────

        get activeLoop(): Loop | null {
            return this.loops.find(l => l.id === this.activeLoopId) ?? null;
        }

        get editingLoop(): Loop | null {
            return this.loops.find(l => l.id === this.editState.loopId) ?? null;
        }

        get isEditing(): boolean {
            return this.editState.loopId !== null;
        }

        // ─── Abstract Methods ──────────────────────────────────────────────────
        // These must be implemented by the concrete component

        abstract getMediaId(): string;
        abstract play(): void;
        abstract pause(): void;
        abstract getCurrentTime(): number;
        abstract getDuration(): number;
        abstract seekTo(time: number): void;
        abstract setPlaybackRate(rate: number): void;
        abstract isMediaReady(): boolean;

        // Optional lifecycle hook for lazy initialization (e.g., YouTube player)
        onFirstExpand?(): void;

        // ─── Abstract Properties ───────────────────────────────────────────────
        // These should be defined as @property() in the concrete component

        abstract readonly storagePrefix: string;
        abstract readonly autoRestore: boolean;
        abstract readonly showShortcuts: boolean;
        /** Media type icon for compact view (e.g., '🎵', '🎬', '📺') */
        abstract readonly mediaIcon: string;

        /** Get the display title for compact view */
        abstract getCompactTitle(): string;

        /** Get optional subtitle (e.g., channel name). Override in subclass. */
        getSubtitle(): string {
            return '';
        }

        // ─── Lifecycle ─────────────────────────────────────────────────────────

        connectedCallback() {
            super.connectedCallback();
            // Make host focusable for keyboard navigation
            if (!this.hasAttribute('tabindex')) {
                this.setAttribute('tabindex', '0');
            }
            // Listen on the element itself instead of window
            this.addEventListener('keydown', this._boundKeyboardHandler);
            this._debouncedSave = createDebouncedSave(this.getMediaId(), this.storagePrefix);
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this.removeEventListener('keydown', this._boundKeyboardHandler);
            document.removeEventListener('click', this._boundDropdownCloseHandler);
            this._cleanupDragListeners();
            this.stopTimeUpdate();
            this.cancelCountdown();
        }

        protected updated(changedProperties: PropertyValues) {
            super.updated(changedProperties);
            // Re-initialize debounced save if media ID changes
            if (changedProperties.has('storagePrefix')) {
                this._debouncedSave = createDebouncedSave(this.getMediaId(), this.storagePrefix);
            }
            // Notify subclass on first expand (useful for lazy initialization like YouTube)
            // Wait for updateComplete to ensure DOM is fully rendered before initializing
            if (changedProperties.has('expanded') && this.expanded && changedProperties.get('expanded') === false) {
                this.updateComplete.then(() => this.onFirstExpand?.());
            }
        }

        // ─── Time Update ───────────────────────────────────────────────────────

        startTimeUpdate() {
            this.stopTimeUpdate();
            this._updateInterval = window.setInterval(() => {
                if (this.isMediaReady()) {
                    this.currentTime = this.getCurrentTime();

                    // Update duration if not set
                    if (this.duration === 0) {
                        const dur = this.getDuration();
                        if (dur > 0) {
                            this.duration = dur;
                        }
                    }

                    this.checkLoopBoundary();
                }
            }, TIMING.FRAME_INTERVAL_MS);
        }

        stopTimeUpdate() {
            if (this._updateInterval) {
                clearInterval(this._updateInterval);
                this._updateInterval = null;
            }
        }

        checkLoopBoundary() {
            // Skip during countdown
            if (this.countdown !== null) return;

            if (this.loopEnabled && this.activeLoop) {
                if (this.currentTime >= this.activeLoop.end) {
                    this.seekTo(this.activeLoop.start);
                }
            }
        }

        // ─── Expand/Collapse ───────────────────────────────────────────────────

        toggleExpanded() {
            this.expanded = !this.expanded;
            this.saveCurrentState();
        }

        /** Focus this player for keyboard shortcuts */
        focus() {
            // Call the native HTMLElement.focus() method to avoid recursion
            HTMLElement.prototype.focus.call(this);
        }

        // ─── Shared View Rendering ──────────────────────────────────────────────

        /** Whether to show the expand button (override in subclasses like audio player) */
        get showExpandButton(): boolean {
            return true;
        }

        /** Header only - title and expand button */
        _renderHeader(): TemplateResult {
            const subtitle = this.getSubtitle();
            return html`
              <div class="lp-compact-header">
                <div class="lp-compact-title-group">
                  <span class="lp-compact-title">${this.mediaIcon} ${this.getCompactTitle()}</span>
                  ${subtitle ? html`<span class="lp-compact-subtitle">${subtitle}</span>` : null}
                </div>
                ${this.showExpandButton ? html`
                  <button 
                    class="lp-expand-btn${this.expanded ? ' expanded' : ''}" 
                    @click=${() => this.toggleExpanded()}
                    title="${this.expanded ? 'Collapse' : 'Expand'}"
                    aria-label="${this.expanded ? 'Collapse player' : 'Expand player'}"
                    aria-expanded="${this.expanded}"
                  >
                    ${chevronDownIcon}
                  </button>
                ` : null}
              </div>
            `;
        }

        /** Controls + Timeline - shown in both compact and full mode */
        _renderControlsAndTimeline(): TemplateResult {
            // Show editable timeline (with drag handles) when expanded
            const isEditable = this.expanded;
            return html`
              ${this._renderControls()}
              ${this._renderTimeline(isEditable)}
            `;
        }

        /** Header + Controls - shown in both compact and full mode (legacy, for audio) */
        _renderCompactView(): TemplateResult {
            // Show editable timeline (with drag handles) when expanded
            const isEditable = this.expanded;
            return html`
              <div class="lp-compact-view">
                <div class="lp-compact-header">
                  <span class="lp-compact-title">${this.mediaIcon} ${this.getCompactTitle()}</span>
                  <button 
                    class="lp-expand-btn${this.expanded ? ' expanded' : ''}" 
                    @click=${() => this.toggleExpanded()}
                    title="${this.expanded ? 'Collapse' : 'Expand'}"
                    aria-label="${this.expanded ? 'Collapse player' : 'Expand player'}"
                    aria-expanded="${this.expanded}"
                  >
                    ${chevronDownIcon}
                  </button>
                </div>
                ${this._renderControls()}
                ${this._renderTimeline(isEditable)}
              </div>
            `;
        }

        /** Playback controls row - just transport controls */
        _renderControls(): TemplateResult {
            return html`
              <div class="lp-controls-row">
                <div class="lp-playback-btns">
                  <button class="btn icon square secondary" @click=${() => this.seek(-ADJUSTMENTS.SEEK_NORMAL_S)} title="Back 5s" aria-label="Seek backward 5 seconds">
                    ${seekBackwardIcon}
                  </button>
                  <button class="btn icon square" @click=${() => this.togglePlayPause()} title="Play/Pause" aria-label="${this.isPlaying ? 'Pause' : 'Play'}">
                    ${this.isPlaying ? pauseIcon : playIcon}
                  </button>
                  <button class="btn icon square secondary" @click=${() => this.seek(ADJUSTMENTS.SEEK_NORMAL_S)} title="Forward 5s" aria-label="Seek forward 5 seconds">
                    ${seekForwardIcon}
                  </button>
                  <button 
                    class="btn icon square ${this.loopEnabled ? '' : 'secondary'}" 
                    @click=${() => this.toggleLoopEnabled()} 
                    title="Toggle loop mode (${this.loopEnabled ? 'ON' : 'OFF'})"
                    aria-label="Toggle loop mode"
                    aria-pressed="${this.loopEnabled}"
                  >
                    ${repeatIcon}
                  </button>
                </div>
                <span class="time-display">
                  ${!this.isMediaReady() && !this.expanded
                    ? html`<span class="lp-expand-hint" @click=${() => this.toggleExpanded()} title="Click to expand">▶ Expand</span>`
                    : html`${formatTime(this.currentTime)} / ${formatTime(this.duration)}`
                }
                </span>
                <select 
                  class="lp-speed-select" 
                  .value=${String(this.playbackSpeed)}
                  @change=${(e: Event) => this.setSpeed(parseFloat((e.target as HTMLSelectElement).value))}
                  title="Playback speed"
                >
                  <option value="2">2x</option>
                  <option value="1.9">1.9x</option>
                  <option value="1.8">1.8x</option>
                  <option value="1.7">1.7x</option>
                  <option value="1.6">1.6x</option>
                  <option value="1.5">1.5x</option>
                  <option value="1.4">1.4x</option>
                  <option value="1.3">1.3x</option>
                  <option value="1.2">1.2x</option>
                  <option value="1.1">1.1x</option>
                  <option value="1">1x</option>
                  <option value="0.9">0.9x</option>
                  <option value="0.8">0.8x</option>
                  <option value="0.7">0.7x</option>
                  <option value="0.6">0.6x</option>
                  <option value="0.5">0.5x</option>
                  <option value="0.4">0.4x</option>
                  <option value="0.3">0.3x</option>
                  <option value="0.2">0.2x</option>
                </select>
              </div>
            `;
        }

        /** Unified timeline with loops - editable in full mode only */
        _renderTimeline(editable: boolean): TemplateResult {
            const zoom = this.getTimelineZoom();
            const zoomDuration = zoom.end - zoom.start;

            // Progress relative to zoom range
            const progressTime = Math.max(zoom.start, Math.min(zoom.end, this.currentTime));
            const progress = zoomDuration > 0 ? ((progressTime - zoom.start) / zoomDuration) * 100 : 0;
            const clampedProgress = Math.max(0, Math.min(100, progress));

            return html`
              <div class="lp-timeline-container ${this.zoomToLoop ? 'zoomed' : ''}" style="anchor-name: --timeline-container">
                ${this.loopEnabled ? html`
                <div class="lp-timeline-loops" style="anchor-name: --timeline-loops">
                  ${this.loops.map(loop => {
                // Calculate loop position relative to zoom range
                const loopStart = Math.max(zoom.start, loop.start);
                const loopEnd = Math.min(zoom.end, loop.end);

                // Skip if loop is completely outside zoom range
                if (loopEnd <= zoom.start || loopStart >= zoom.end) return null;

                const left = zoomDuration > 0 ? ((loopStart - zoom.start) / zoomDuration) * 100 : 0;
                const width = zoomDuration > 0 ? ((loopEnd - loopStart) / zoomDuration) * 100 : 0;
                const isActive = loop.id === this.activeLoopId;

                return html`
                      <div
                        class="lp-timeline-loop ${isActive ? 'active' : ''}"
                        style="left: ${left}%; width: ${width}%; background: ${loop.color}"
                        @click=${(e: Event) => { e.stopPropagation(); this._handleLoopClick(loop.id); }}
                        @dblclick=${(e: Event) => { e.stopPropagation(); this._handleLoopDblClick(loop.id); }}
                        title="${loop.label}"
                      >
                        ${width > 8 ? loop.label : ''}
                      </div>
                    `;
            })}
                </div>
                ` : null}
                <div 
                  class="lp-timeline-bar" 
                  role="slider"
                  aria-label="Playback position"
                  aria-valuemin="0"
                  aria-valuemax="${Math.round(this.duration)}"
                  aria-valuenow="${Math.round(this.currentTime)}"
                  aria-valuetext="${formatTime(this.currentTime)} of ${formatTime(this.duration)}"
                  @click=${(e: MouseEvent) => this._handleTimelineClick(e)}
                  @mousemove=${(e: MouseEvent) => this._handleTimelineHover(e)}
                  @mouseleave=${() => this._hoverPreviewPercent = null}
                  @touchstart=${(e: TouchEvent) => this._handleTimelineTouchStart(e)}
                  @touchmove=${(e: TouchEvent) => this._handleTimelineTouchMove(e)}
                  @touchend=${(e: TouchEvent) => this._handleTimelineTouchEnd(e)}
                >
                  <div class="lp-timeline-progress" style="width: ${clampedProgress}%"></div>
                  <div class="lp-timeline-position" style="left: ${clampedProgress}%"></div>
                  ${this._hoverPreviewPercent !== null ? html`
                    <div class="lp-timeline-preview" style="left: ${this._hoverPreviewPercent}%">
                      <span class="lp-preview-time">${formatTime(zoom.start + (this._hoverPreviewPercent / 100) * zoomDuration)}</span>
                    </div>
                  ` : null}
                </div>
                ${this.loopEnabled && this.activeLoop ? this._renderDragHandles() : null}
              </div>
            `;
        }

        /** Drag handles for editing loop boundaries - shown when a loop is selected */
        _renderDragHandles(): TemplateResult | null {
            const loop = this.activeLoop;
            if (!loop || this.duration <= 0) return null;

            const zoom = this.getTimelineZoom();
            const zoomDuration = zoom.end - zoom.start;

            // Calculate percentages relative to zoomed view
            const startPercent = ((loop.start - zoom.start) / zoomDuration) * 100;
            const endPercent = ((loop.end - zoom.start) / zoomDuration) * 100;
            const activeBoundary = this.editState.boundary ?? 'end';

            return html`
              <div class="lp-timeline-handles ${this.zoomToLoop ? 'zoomed' : ''}">
                <div 
                  class="lp-timeline-loop-region" 
                  style="left: ${startPercent}%; width: ${endPercent - startPercent}%"
                ></div>
                
                <div 
                  class="lp-timeline-handle start ${this.draggingBoundary === 'start' ? 'dragging' : ''} ${activeBoundary === 'start' ? 'active' : ''}"
                  style="left: ${startPercent}%"
                  @mousedown=${(e: MouseEvent) => this.startDrag('start', loop.id, e)}
                  @touchstart=${(e: TouchEvent) => this.startDrag('start', loop.id, e)}
                  @click=${() => this.editState = { loopId: loop.id, boundary: 'start' }}
                  title="Start: ${formatTimeMs(loop.start)}"
                >
                  ${chevronLeftIcon}
                </div>
                
                <div 
                  class="lp-timeline-handle end ${this.draggingBoundary === 'end' ? 'dragging' : ''} ${activeBoundary === 'end' ? 'active' : ''}"
                  style="left: ${endPercent}%"
                  @mousedown=${(e: MouseEvent) => this.startDrag('end', loop.id, e)}
                  @touchstart=${(e: TouchEvent) => this.startDrag('end', loop.id, e)}
                  @click=${() => this.editState = { loopId: loop.id, boundary: 'end' }}
                  title="End: ${formatTimeMs(loop.end)}"
                >
                  ${chevronRightIcon}
                </div>
              </div>
            `;
        }

        /** Handle click on timeline to seek */
        _handleTimelineClick(e: MouseEvent) {
            if (!this.duration) return;
            const zoom = this.getTimelineZoom();
            const zoomDuration = zoom.end - zoom.start;
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const percent = (e.clientX - rect.left) / rect.width;
            const seekTime = zoom.start + percent * zoomDuration;
            this.seekTo(seekTime);
            this._hoverPreviewPercent = null; // Clear preview after seeking
        }

        /** Handle hover on timeline to show preview cursor */
        _handleTimelineHover(e: MouseEvent) {
            if (!this.duration) return;
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const percent = ((e.clientX - rect.left) / rect.width) * 100;
            this._hoverPreviewPercent = Math.max(0, Math.min(100, percent));
        }

        /** Handle touch start on timeline - show preview */
        _handleTimelineTouchStart(e: TouchEvent) {
            if (!this.duration) return;
            const touch = e.touches[0];
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const percent = ((touch.clientX - rect.left) / rect.width) * 100;
            this._hoverPreviewPercent = Math.max(0, Math.min(100, percent));
        }

        /** Handle touch move on timeline - update preview */
        _handleTimelineTouchMove(e: TouchEvent) {
            if (!this.duration) return;
            e.preventDefault(); // Prevent scrolling while dragging
            const touch = e.touches[0];
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const percent = ((touch.clientX - rect.left) / rect.width) * 100;
            this._hoverPreviewPercent = Math.max(0, Math.min(100, percent));
        }

        /** Handle touch end on timeline - seek to position and clear preview */
        _handleTimelineTouchEnd(e: TouchEvent) {
            if (!this.duration || this._hoverPreviewPercent === null) return;
            const zoom = this.getTimelineZoom();
            const zoomDuration = zoom.end - zoom.start;
            const seekTime = zoom.start + (this._hoverPreviewPercent / 100) * zoomDuration;
            this.seekTo(seekTime);
            this._hoverPreviewPercent = null;
        }

        /** Handle single click on loop - activate it */
        _handleLoopClick(loopId: string) {
            this.activateLoop(loopId);
        }

        /** Handle double click on loop - activate and start playback */
        _handleLoopDblClick(loopId: string) {
            this.activateLoop(loopId);
            const loop = this.loops.find(l => l.id === loopId);
            if (loop) {
                this.seekTo(loop.start);
                this.play();
            }
        }

        /** Edit panel for loop boundary adjustments - shows when loop mode is enabled */
        _renderEditPanel(): TemplateResult | null {
            if (!this.loopEnabled) return null;

            const loop = this.activeLoop;
            const hasMultipleLoops = this.loops.length > 1;

            return html`
              <div class="lp-edit-toolbar">
                <!-- Loop actions row -->
                <div class="lp-loop-actions">
                  <!-- Loop selector (icon only when multiple loops) -->
                  ${hasMultipleLoops ? html`
                    <div class="lp-loop-selector">
                      <button 
                        class="btn icon square secondary lp-loop-selector-btn"
                        style="--loop-color: ${loop?.color ?? 'var(--lp-accent)'}"
                        @click=${(e: Event) => this._toggleLoopDropdown(e)}
                        title="Select loop (${this.loops.findIndex(l => l.id === this.activeLoopId) + 1}/${this.loops.length})"
                        aria-label="Select loop"
                        aria-haspopup="listbox"
                      >
                        ${listIcon}
                      </button>
                      ${this._loopDropdownOpen ? html`
                        <div class="lp-loop-dropdown" role="listbox">
                          ${this.loops.map((l, i) => html`
                            <button 
                              class="lp-loop-dropdown-item ${l.id === this.activeLoopId ? 'active' : ''}"
                              style="--loop-color: ${l.color}"
                              role="option"
                              aria-selected="${l.id === this.activeLoopId}"
                              @click=${() => { this.activateLoop(l.id); this._loopDropdownOpen = false; }}
                            >
                              <span class="lp-loop-color-dot"></span>
                              ${i + 1}. ${l.label}
                            </button>
                          `)}
                        </div>
                      ` : null}
                    </div>
                  ` : null}
                  
                  <!-- Add loop -->
                  <button 
                    class="btn icon square secondary" 
                    @click=${() => this._addNewLoop()} 
                    title="Add new loop (N)"
                    aria-label="Add new loop"
                  >
                    ${plusIcon}
                  </button>
                  
                  <!-- Countdown toggle -->
                  <button
                    class="btn icon square ${this.countdownEnabled ? '' : 'secondary'}"
                    @click=${() => this.toggleCountdownEnabled()}
                    title="Toggle countdown ${this.countdownEnabled ? 'ON' : 'OFF'}"
                    aria-label="Toggle countdown"
                    aria-pressed="${this.countdownEnabled}"
                  >
                    ${countdownIcon}
                  </button>
                  
                  ${loop ? html`
                    <!-- Separator -->
                    <span class="lp-separator"></span>
                    
                    <!-- Play from start -->
                    <button class="btn icon square" @click=${() => { this.startCountdownThenPlay(loop.start); }} title="Play from start (R)" aria-label="Play from loop start">${playIcon}</button>
                    
                    <!-- Zoom -->
                    <button class="btn icon square secondary ${this.zoomToLoop ? 'active' : ''}" @click=${() => this.toggleZoom()} title="Zoom to loop (Z)" aria-label="Toggle zoom to loop" aria-pressed="${this.zoomToLoop}">${zoomIcon}</button>
                    
                    <!-- Set boundaries -->
                    <button class="btn icon square secondary" @click=${() => this.setBoundaryToCurrent('start')} title="Set start to current (S)" aria-label="Set loop start to current time">${timerIcon}<small>S</small></button>
                    <button class="btn icon square secondary" @click=${() => this.setBoundaryToCurrent('end')} title="Set end to current (E)" aria-label="Set loop end to current time">${timerIcon}<small>E</small></button>
                    
                    <!-- Delete -->
                    <button class="btn icon square secondary" @click=${() => this._confirmDeleteLoop(loop.id)} title="Delete (Del)" aria-label="Delete loop">${trashIcon}</button>
                  ` : null}
                </div>
                
                ${loop ? html`
                  <!-- Loop info row -->
                  <div class="lp-loop-info">
                    <!-- Color dot -->
                    <span class="lp-loop-color-dot" style="--loop-color: ${loop.color}"></span>
                    
                    <!-- Editable loop name -->
                    ${this._editingLoopName ? html`
                      <input 
                        type="text" 
                        class="lp-edit-name-input"
                        aria-label="Loop name"
                        .value=${loop.label}
                        @keydown=${(e: KeyboardEvent) => {
                            if (e.key === 'Enter') { this._finishEditingName(e); e.preventDefault(); }
                            if (e.key === 'Escape') { this._cancelEditing(); }
                            e.stopPropagation();
                        }}
                        @mousedown=${(e: Event) => e.stopPropagation()}
                      />
                    ` : html`
                      <span 
                        class="lp-edit-title" 
                        role="button"
                        tabindex="0"
                        @click=${() => this._startEditingName()}
                        @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this._startEditingName(); }}
                        title="Click to rename"
                        aria-label="Loop name: ${loop.label}, click to edit"
                      >${loop.label}</span>
                    `}
                    
                    <!-- Editable times -->
                    <span class="lp-loop-times">
                      <span 
                        class="lp-time-value ${this._editingStartTime ? 'editing' : ''}" 
                        role="button"
                        tabindex="0"
                        style="anchor-name: --start-time"
                        @click=${() => this._startEditingStartTime()}
                        @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this._startEditingStartTime(); }}
                        title="Click to edit"
                        aria-label="Start time: ${formatTimeMs(loop.start)}, click to edit"
                      >${formatTimeMs(loop.start)}</span>
                      
                      <!-- Start time popover (native) -->
                      <div 
                        id="start-time-popover"
                        popover
                        class="popover compact bottom"
                        style="position-anchor: --timeline-container"
                        @toggle=${(e: ToggleEvent) => { if (e.newState === 'closed') this._handleStartTimePopoverClosed(); }}
                      >
                        <div class="popover-header">
                          <span class="popover-title">Start Time</span>
                        </div>
                        <input 
                          type="text" 
                          class="popover-input"
                          data-test="js-edit-start-time-input"
                          aria-label="Start time"
                          .value=${this._editingStartTime ? formatTimeMs(loop.start) : ''}
                          @keydown=${(e: KeyboardEvent) => {
                        if (e.key === 'Enter') { this._finishEditingStartTime(e); e.preventDefault(); }
                        if (e.key === 'Escape') { this._closeStartTimePopover(); }
                        e.stopPropagation();
                    }}
                        />
                        <div class="popover-adjust-row">
                          <button class="btn xs secondary" @click=${() => this._adjustStartTime(-1)} title="-1s">−1s</button>
                          <button class="btn xs secondary" @click=${() => this._adjustStartTime(-0.1)} title="-0.1s">−.1</button>
                          <button class="btn xs secondary" @click=${() => this._setStartTimeToCurrent()} title="Set to current time">Now</button>
                          <button class="btn xs secondary" @click=${() => this._adjustStartTime(0.1)} title="+0.1s">+.1</button>
                          <button class="btn xs secondary" @click=${() => this._adjustStartTime(1)} title="+1s">+1s</button>
                        </div>
                        <div class="popover-footer">
                          <button class="btn ghost" @click=${() => this._previewStartTime()} title="Play from start">▶ Play</button>
                          <button class="btn" @click=${(e: Event) => this._finishEditingStartTime(e)}>Done</button>
                        </div>
                      </div>
                      
                      <span class="lp-time-separator">–</span>
                      
                      <span 
                        class="lp-time-value ${this._editingEndTime ? 'editing' : ''}" 
                        role="button"
                        tabindex="0"
                        style="anchor-name: --end-time"
                        @click=${() => this._startEditingEndTime()}
                        @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this._startEditingEndTime(); }}
                        title="Click to edit"
                        aria-label="End time: ${formatTimeMs(loop.end)}, click to edit"
                      >${formatTimeMs(loop.end)}</span>
                      
                      <!-- End time popover (native) -->
                      <div 
                        id="end-time-popover"
                        popover
                        class="popover compact bottom"
                        style="position-anchor: --timeline-container"
                        @toggle=${(e: ToggleEvent) => { if (e.newState === 'closed') this._handleEndTimePopoverClosed(); }}
                      >
                        <div class="popover-header">
                          <span class="popover-title">End Time</span>
                        </div>
                        <input 
                          type="text" 
                          class="popover-input"
                          data-test="js-edit-end-time-input"
                          aria-label="End time"
                          .value=${this._editingEndTime ? formatTimeMs(loop.end) : ''}
                          @keydown=${(e: KeyboardEvent) => {
                        if (e.key === 'Enter') { this._finishEditingEndTime(e); e.preventDefault(); }
                        if (e.key === 'Escape') { this._closeEndTimePopover(); }
                        e.stopPropagation();
                    }}
                        />
                        <div class="popover-adjust-row">
                          <button class="btn xs secondary" @click=${() => this._adjustEndTime(-1)} title="-1s">−1s</button>
                          <button class="btn xs secondary" @click=${() => this._adjustEndTime(-0.1)} title="-0.1s">−.1</button>
                          <button class="btn xs secondary" @click=${() => this._setEndTimeToCurrent()} title="Set to current time">Now</button>
                          <button class="btn xs secondary" @click=${() => this._adjustEndTime(0.1)} title="+0.1s">+.1</button>
                          <button class="btn xs secondary" @click=${() => this._adjustEndTime(1)} title="+1s">+1s</button>
                        </div>
                        <div class="popover-footer">
                          <button class="btn ghost" @click=${() => this._previewEndTime()} title="Preview last 3s">▶ Preview</button>
                          <button class="btn" @click=${(e: Event) => this._finishEditingEndTime(e)}>Done</button>
                        </div>
                      </div>
                    </span>
                  </div>
                ` : null}
              </div>
            `;
        }

        /** Start editing loop name with click-outside handler */
        private _startEditingName() {
            this._editingLoopName = true;
            // Use setTimeout to wait for render, then focus and select
            setTimeout(() => {
                const input = this.shadowRoot?.querySelector('.lp-edit-name-input') as HTMLInputElement | null;
                if (input) {
                    input.focus();
                    input.select();
                }
                document.addEventListener('mousedown', this._boundClickOutsideHandler);
            }, 0);
        }

        /** Start editing start time */
        private _startEditingStartTime() {
            // If end time is being edited, close it first (saving changes)
            if (this._editingEndTime) {
                this._closeEndTimePopover();
            }

            this._editingStartTime = true;
            // Show native popover
            this.updateComplete.then(() => {
                const popover = this.shadowRoot?.querySelector('#start-time-popover') as HTMLElement | null;
                popover?.showPopover?.();
                this._focusTimeInput('js-edit-start-time-input');
            });
        }

        /** Start editing end time */
        private _startEditingEndTime() {
            // If start time is being edited, close it first (saving changes)
            if (this._editingStartTime) {
                this._closeStartTimePopover();
            }

            this._editingEndTime = true;
            // Show native popover
            this.updateComplete.then(() => {
                const popover = this.shadowRoot?.querySelector('#end-time-popover') as HTMLElement | null;
                popover?.showPopover?.();
                this._focusTimeInput('js-edit-end-time-input');
            });
        }

        /** Close start time popover */
        private _closeStartTimePopover() {
            const popover = this.shadowRoot?.querySelector('#start-time-popover') as HTMLElement | null;
            popover?.hidePopover?.();
        }

        /** Close end time popover */
        private _closeEndTimePopover() {
            const popover = this.shadowRoot?.querySelector('#end-time-popover') as HTMLElement | null;
            popover?.hidePopover?.();
        }

        /** Focus time input after render */
        private _focusTimeInput(testId: string) {
            setTimeout(() => {
                const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
                if (!isTouchDevice) {
                    const input = this.shadowRoot?.querySelector(`[data-test="${testId}"]`) as HTMLInputElement | null;
                    if (input) {
                        input.focus();
                        input.select();
                    }
                }
            }, 0);
        }

        /** Handle start time popover closed */
        private _handleStartTimePopoverClosed() {
            const input = this.shadowRoot?.querySelector('[data-test="js-edit-start-time-input"]') as HTMLInputElement | null;
            if (input && this.activeLoop) {
                this._saveTimeEdit('start', input.value);
            }
            this._editingStartTime = false;
            this._restoreFocus();
        }

        /** Handle end time popover closed */
        private _handleEndTimePopoverClosed() {
            const input = this.shadowRoot?.querySelector('[data-test="js-edit-end-time-input"]') as HTMLInputElement | null;
            if (input && this.activeLoop) {
                this._saveTimeEdit('end', input.value);
            }
            this._editingEndTime = false;
            this._restoreFocus();
        }

        /** Save time edit value without resetting state */
        private _saveTimeEdit(boundary: 'start' | 'end', timeStr: string) {
            const seconds = this._parseTimeString(timeStr.trim());
            if (seconds !== null && this.activeLoop) {
                if (boundary === 'start') {
                    const clampedStart = Math.max(0, Math.min(this.activeLoop.end - 0.1, seconds));
                    this.updateLoop(this.activeLoop.id, { start: clampedStart });
                } else {
                    const clampedEnd = Math.min(this.duration, Math.max(this.activeLoop.start + 0.1, seconds));
                    this.updateLoop(this.activeLoop.id, { end: clampedEnd });
                }
            }
        }

        /** Cancel editing without saving */
        private _cancelEditing() {
            this._editingLoopName = false;
            this._editingStartTime = false;
            this._editingEndTime = false;
            document.removeEventListener('mousedown', this._boundClickOutsideHandler);
            // Restore focus to player
            this._restoreFocus();
        }

        /** Restore focus to the player component for keyboard shortcuts */
        private _restoreFocus() {
            // Wait for Lit to finish re-rendering (removing popover/dialog from DOM)
            this.updateComplete.then(() => {
                // Call native HTMLElement.focus() to avoid recursion
                HTMLElement.prototype.focus.call(this);
            });
        }

        /** Toggle loop selector dropdown */
        private _toggleLoopDropdown(e: Event) {
            e.stopPropagation();
            this._loopDropdownOpen = !this._loopDropdownOpen;
            if (this._loopDropdownOpen) {
                setTimeout(() => {
                    document.addEventListener('click', this._boundDropdownCloseHandler);
                }, 0);
            } else {
                document.removeEventListener('click', this._boundDropdownCloseHandler);
            }
        }

        /** Close loop dropdown */
        private _closeLoopDropdown() {
            this._loopDropdownOpen = false;
            document.removeEventListener('click', this._boundDropdownCloseHandler);
        }

        /** Handle click outside to finish editing (for name editing only - time uses native popover) */
        private _handleClickOutside(e: MouseEvent) {
            // Check if the click is outside the shadow DOM
            const path = e.composedPath();
            const isInsideNameInput = path.some(el =>
                el instanceof HTMLInputElement && el.classList.contains('lp-edit-name-input')
            );

            if (!isInsideNameInput) {
                // Find and save the current input value before closing
                const nameInput = this.shadowRoot?.querySelector('.lp-edit-name-input') as HTMLInputElement | null;

                if (this._editingLoopName && nameInput) {
                    this._finishEditingName({ target: nameInput } as unknown as Event);
                }

                document.removeEventListener('mousedown', this._boundClickOutsideHandler);
                // Note: focus is restored by _finishEditingName
            }
        }

        /** Finish editing loop name */
        private _finishEditingName(e: Event) {
            const input = e.target as HTMLInputElement;
            const newName = input.value.trim();
            if (newName && this.activeLoop) {
                this.updateLoop(this.activeLoop.id, { label: newName });
            }
            this._editingLoopName = false;
            document.removeEventListener('mousedown', this._boundClickOutsideHandler);
            this._restoreFocus();
        }

        /** Finish editing start time - parse time string and update loop */
        private _finishEditingStartTime(e: Event) {
            const input = e.target as HTMLInputElement;
            this._saveTimeEdit('start', input.value);
            this._editingStartTime = false;
            this._restoreFocus();
        }

        /** Finish editing end time - parse time string and update loop */
        private _finishEditingEndTime(e: Event) {
            const input = e.target as HTMLInputElement;
            this._saveTimeEdit('end', input.value);
            this._editingEndTime = false;
            this._restoreFocus();
        }

        /** Adjust start time value while editing */
        private _adjustStartTime(delta: number) {
            if (!this._editingStartTime || !this.activeLoop) return;

            const currentTime = this.activeLoop.start;
            const newTime = Math.max(0, Math.min(this.activeLoop.end - 0.1, currentTime + delta));
            this.updateLoop(this.activeLoop.id, { start: newTime });

            const input = this.shadowRoot?.querySelector('[data-test="js-edit-start-time-input"]') as HTMLInputElement | null;
            if (input) {
                input.value = formatTimeMs(newTime);
            }
        }

        /** Adjust end time value while editing */
        private _adjustEndTime(delta: number) {
            if (!this._editingEndTime || !this.activeLoop) return;

            const currentTime = this.activeLoop.end;
            const newTime = Math.min(this.duration, Math.max(this.activeLoop.start + 0.1, currentTime + delta));
            this.updateLoop(this.activeLoop.id, { end: newTime });

            const input = this.shadowRoot?.querySelector('[data-test="js-edit-end-time-input"]') as HTMLInputElement | null;
            if (input) {
                input.value = formatTimeMs(newTime);
            }
        }

        /** Set start time to current playback position */
        private _setStartTimeToCurrent() {
            if (!this._editingStartTime || !this.activeLoop) return;
            const clampedStart = Math.max(0, Math.min(this.activeLoop.end - 0.1, this.currentTime));
            this.updateLoop(this.activeLoop.id, { start: clampedStart });
            const input = this.shadowRoot?.querySelector('[data-test="js-edit-start-time-input"]') as HTMLInputElement | null;
            if (input) input.value = formatTimeMs(clampedStart);
        }

        /** Set end time to current playback position */
        private _setEndTimeToCurrent() {
            if (!this._editingEndTime || !this.activeLoop) return;
            const clampedEnd = Math.min(this.duration, Math.max(this.activeLoop.start + 0.1, this.currentTime));
            this.updateLoop(this.activeLoop.id, { end: clampedEnd });
            const input = this.shadowRoot?.querySelector('[data-test="js-edit-end-time-input"]') as HTMLInputElement | null;
            if (input) input.value = formatTimeMs(clampedEnd);
        }

        /** Preview start time - seek and play */
        private _previewStartTime() {
            if (!this._editingStartTime || !this.activeLoop) return;
            const input = this.shadowRoot?.querySelector('[data-test="js-edit-start-time-input"]') as HTMLInputElement | null;
            if (input) {
                const seconds = this._parseTimeString(input.value.trim());
                if (seconds !== null) {
                    const clampedStart = Math.max(0, Math.min(this.activeLoop.end - 0.1, seconds));
                    this.updateLoop(this.activeLoop.id, { start: clampedStart });
                }
            }
            this.seekTo(this.activeLoop.start);
            this.play();
        }

        /** Preview end time - seek to 3s before and play */
        private _previewEndTime() {
            if (!this._editingEndTime || !this.activeLoop) return;
            const input = this.shadowRoot?.querySelector('[data-test="js-edit-end-time-input"]') as HTMLInputElement | null;
            if (input) {
                const seconds = this._parseTimeString(input.value.trim());
                if (seconds !== null) {
                    const clampedEnd = Math.min(this.duration, Math.max(this.activeLoop.start + 0.1, seconds));
                    this.updateLoop(this.activeLoop.id, { end: clampedEnd });
                }
            }
            const previewStart = Math.max(this.activeLoop.start, this.activeLoop.end - TIMING.PREVIEW_BEFORE_END_S);
            this.seekTo(previewStart);
            this.play();
        }

        /** Parse time string like "1:23.456" or "83.456" to seconds */
        private _parseTimeString(str: string): number | null {
            // Try mm:ss.ms format
            const colonMatch = str.match(/^(\d+):(\d+(?:\.\d+)?)$/);
            if (colonMatch) {
                const minutes = parseInt(colonMatch[1], 10);
                const seconds = parseFloat(colonMatch[2]);
                return minutes * 60 + seconds;
            }

            // Try plain seconds format
            const num = parseFloat(str);
            if (!isNaN(num) && num >= 0) {
                return num;
            }

            return null;
        }

        /** Start long-press detection for mobile label editing */
        private _startLongPress(e: TouchEvent) {
            e.preventDefault();
            this._cancelLongPress();
            this._longPressTimer = window.setTimeout(() => {
                this._startEditingName();
                this._longPressTimer = null;
            }, 500);  // 500ms long-press
        }

        /** Cancel long-press detection */
        private _cancelLongPress() {
            if (this._longPressTimer !== null) {
                clearTimeout(this._longPressTimer);
                this._longPressTimer = null;
            }
        }

        /** Empty state when no loop is selected - just shows hint */
        _renderLoopList(): TemplateResult {
            return html``;
        }

        /** Keyboard shortcuts help text */
        _renderShortcuts(): TemplateResult | null {
            if (!this.showShortcuts) return null;

            return html`
              <div class="lp-shortcuts-help">
                <strong>⌨️ Shortcuts:</strong>
                <strong>Space</strong>=Play/Pause •
                <strong>←/→</strong>=±5s (Shift=±0.1s) •
                <strong>↑/↓</strong>=Speed±0.1 •
                <strong>L</strong>=Toggle Loop •
                <strong>C</strong>=Toggle Countdown •
                <strong>R</strong>=Return •
                <strong>S/E</strong>=Set Start/End •
                <strong>N</strong>=New Loop •
                <strong>Z</strong>=Zoom •
                <strong>1-9</strong>=Select Loop •
                <strong>0/Esc</strong>=Deselect •
                ${this.activeLoopId ? html`<strong>Tab</strong>=Switch Boundary • <strong>Shift+←/→</strong>=Adjust` : ''}
              </div>
              </div>
            `;
        }

        // ─── Playback Controls ─────────────────────────────────────────────────

        togglePlayPause() {
            // If media not ready (e.g., YouTube in collapsed mode), expand first and queue autoplay
            if (!this.isMediaReady()) {
                if (!this.expanded) {
                    this._pendingAutoPlay = true;
                    this.toggleExpanded();
                }
                return;
            }
            if (this.isPlaying) {
                this.pause();
            } else {
                this.play();
            }
        }

        /** Called by subclass when media becomes ready - checks for pending autoplay */
        _onMediaReady() {
            if (this._pendingAutoPlay) {
                this._pendingAutoPlay = false;
                this.play();
            }
        }

        seek(seconds: number) {
            if (!this.isMediaReady()) return;
            const newTime = Math.max(0, Math.min(this.duration, this.currentTime + seconds));
            this.seekTo(newTime);
        }

        setSpeed(speed: number) {
            const clampedSpeed = Math.max(ADJUSTMENTS.SPEED_MIN, Math.min(ADJUSTMENTS.SPEED_MAX, speed));
            const roundedSpeed = Math.round(clampedSpeed * 10) / 10;
            this.playbackSpeed = roundedSpeed;
            if (this.isMediaReady()) {
                this.setPlaybackRate(roundedSpeed);
            }
            this.saveCurrentState();
        }

        adjustSpeed(delta: number) {
            this.setSpeed(this.playbackSpeed + delta);
        }

        // ─── Countdown ─────────────────────────────────────────────────────────

        returnToLoopStart() {
            if (this.activeLoop) {
                this.startCountdownThenPlay(this.activeLoop.start);
            }
        }

        startCountdownThenPlay(startTime: number) {
            this.cancelCountdown();
            if (!this.countdownEnabled) {
                const clampedStart = Math.max(0, Math.min(this.duration, startTime));
                this.seekTo(clampedStart);
                this.play();
                return;
            }

            this._pendingStartTime = startTime;
            this.pause();
            this.countdown = TIMING.COUNTDOWN_SECONDS;

            this._countdownTimer = window.setInterval(() => {
                if (this.countdown !== null && this.countdown > 1) {
                    this.countdown--;
                } else {
                    this.countdown = null;
                    if (this._countdownTimer) {
                        clearInterval(this._countdownTimer);
                        this._countdownTimer = null;
                    }
                    if (this._pendingStartTime !== null) {
                        this.seekTo(this._pendingStartTime);
                        this.play();
                        this._pendingStartTime = null;
                    }
                }
            }, 1000);
        }

        cancelCountdown() {
            if (this._countdownTimer) {
                clearInterval(this._countdownTimer);
                this._countdownTimer = null;
            }
            this.countdown = null;
            this._pendingStartTime = null;
        }

        toggleCountdownEnabled() {
            this.countdownEnabled = !this.countdownEnabled;
            if (!this.countdownEnabled) {
                this.cancelCountdown();
            }
            this.saveCurrentState();
        }

        // ─── Loop Management ───────────────────────────────────────────────────

        /** Add a new loop from current position - used by control bar button */
        _addNewLoop() {
            const loop = this.createLoop(this.currentTime, this.duration);
            this.activateLoop(loop.id);
            this.enterEditMode(loop.id, 'end');
            this.dispatchEvent(new CustomEvent('loop-created', {
                detail: { loop },
                bubbles: true,
                composed: true,
            }));
        }

        createLoop(start: number, end: number): Loop {
            const loop: Loop = {
                id: generateId(),
                label: `Loop ${this.loops.length + 1}`,
                start,
                end,
                color: getNextLoopColor(this.loops.length),
            };
            this.loops = [...this.loops, loop];
            this.saveCurrentState();
            return loop;
        }

        updateLoop(loopId: string, updates: Partial<Loop>) {
            this.loops = this.loops.map(loop =>
                loop.id === loopId ? { ...loop, ...updates } : loop
            );
            this.saveCurrentState();
        }

        deleteLoop(loopId: string) {
            this.loops = this.loops.filter(l => l.id !== loopId);
            if (this.activeLoopId === loopId) {
                this.activeLoopId = null;
            }
            if (this.editState.loopId === loopId) {
                this.exitEditMode();
            }
            this.saveCurrentState();
        }

        private _confirmDeleteLoop(loopId: string) {
            this._pendingDeleteLoopId = loopId;
            // Show native dialog
            this.updateComplete.then(() => {
                const dialog = this.shadowRoot?.querySelector('#delete-loop-dialog') as HTMLDialogElement | null;
                dialog?.showModal?.();
            });
        }

        private _handleDeleteDialogConfirm() {
            if (this._pendingDeleteLoopId) {
                this.deleteLoop(this._pendingDeleteLoopId);
            }
            this._closeDeleteDialog();
        }

        private _closeDeleteDialog() {
            const dialog = this.shadowRoot?.querySelector('#delete-loop-dialog') as HTMLDialogElement | null;
            dialog?.close?.();
            this._pendingDeleteLoopId = null;
            this._restoreFocus();
        }

        _renderDeleteDialog(): TemplateResult | null {
            const loop = this._pendingDeleteLoopId ? this.loops.find(l => l.id === this._pendingDeleteLoopId) : null;
            const label = loop?.label ?? 'this loop';

            return html`
              <dialog id="delete-loop-dialog" class="dialog" @close=${() => { this._pendingDeleteLoopId = null; this._restoreFocus(); }}>
                <div class="dialog-header">
                  <span class="dialog-title">Delete loop?</span>
                </div>
                <div class="dialog-body">
                  <p>Are you sure you want to delete <strong>${label}</strong>?</p>
                </div>
                <div class="dialog-footer">
                  <button class="btn ghost" @click=${() => this._closeDeleteDialog()}>Cancel</button>
                  <button class="btn" style="--color-accent: var(--color-danger);" @click=${() => this._handleDeleteDialogConfirm()}>Delete loop</button>
                </div>
              </dialog>
            `;
        }

        activateLoop(loopId: string | null) {
            this.activeLoopId = loopId;
            if (loopId) {
                this.loopEnabled = true;
                // Sync editState to the new active loop
                this.editState = { loopId, boundary: 'end' };
            } else {
                this.editState = { loopId: null, boundary: null };
            }
            this.saveCurrentState();
        }

        toggleLoopEnabled() {
            this.loopEnabled = !this.loopEnabled;

            // Auto-create a full-duration loop when enabling with no loops
            if (this.loopEnabled && this.loops.length === 0 && this.duration > 0) {
                const loop = this.createLoop(0, this.duration);
                this.activateLoop(loop.id);
            } else if (this.loopEnabled && this.loops.length > 0 && !this.activeLoopId) {
                // Always have a selected loop when loop mode is on
                this.activateLoop(this.loops[0].id);
            }

            // Restore zoom state when re-enabling loop mode
            if (this.loopEnabled && this._zoomWasEnabled) {
                this.zoomToLoop = true;
            }

            // Deselect loop and disable zoom when disabling loop mode
            if (!this.loopEnabled) {
                // Remember zoom state before disabling
                this._zoomWasEnabled = this.zoomToLoop;
                this.zoomToLoop = false;
                this.activeLoopId = null;
                this.exitEditMode();
            }

            this.saveCurrentState();
        }

        selectLoopByIndex(index: number) {
            if (index === 0) {
                // Only deselect if loop mode is off
                if (!this.loopEnabled) {
                    this.activateLoop(null);
                }
            } else if (index <= this.loops.length) {
                this.activateLoop(this.loops[index - 1].id);
            }
        }

        // ─── Edit Mode ─────────────────────────────────────────────────────────

        enterEditMode(loopId: string, boundary: 'start' | 'end') {
            if (this.editState.loopId !== loopId) {
                const loop = this.loops.find(l => l.id === loopId);
                if (loop) {
                    this._lockedZoom = this._calculateZoomForLoop(loop);
                }
            }
            this.editState = { loopId, boundary };
        }

        exitEditMode() {
            this.editState = { loopId: null, boundary: null };
            this._lockedZoom = null;
        }

        private _setLoopStart() {
            if (this.activeLoop) {
                this.updateLoop(this.activeLoop.id, { start: this.currentTime });
                this.enterEditMode(this.activeLoop.id, 'start');
            } else {
                const loop = this.createLoop(this.currentTime, this.duration);
                this.activateLoop(loop.id);
                this.enterEditMode(loop.id, 'start');
            }
        }

        private _setLoopEnd() {
            if (this.activeLoop) {
                this.updateLoop(this.activeLoop.id, { end: this.currentTime });
                this.enterEditMode(this.activeLoop.id, 'end');
            } else {
                const loop = this.createLoop(0, this.currentTime);
                this.activateLoop(loop.id);
                this.enterEditMode(loop.id, 'end');
            }
        }

        adjustEditingBoundary(delta: number) {
            // Use activeLoop (selected loop) for boundary adjustment
            const loop = this.activeLoop;
            if (!loop) return;

            // Default to 'end' boundary if not explicitly set
            const boundary = this.editState.boundary ?? 'end';

            if (boundary === 'start') {
                const newStart = Math.max(0, Math.min(loop.end - TIMELINE.MIN_LOOP_DURATION, loop.start + delta));
                this.updateLoop(loop.id, { start: newStart });
            } else {
                const newEnd = Math.min(this.duration, Math.max(loop.start + TIMELINE.MIN_LOOP_DURATION, loop.end + delta));
                this.updateLoop(loop.id, { end: newEnd });
            }

            // Ensure edit state is synced with active loop
            if (this.editState.loopId !== loop.id) {
                this.editState = { loopId: loop.id, boundary };
            }
        }

        switchEditBoundary() {
            if (!this.activeLoopId) return;
            const currentBoundary = this.editState.boundary ?? 'end';
            this.editState = {
                loopId: this.activeLoopId,
                boundary: currentBoundary === 'start' ? 'end' : 'start',
            };
        }

        setBoundaryToCurrent(boundary: 'start' | 'end') {
            if (!this.editingLoop) return;

            const loop = this.editingLoop;
            if (boundary === 'start') {
                const newStart = Math.min(this.currentTime, loop.end - TIMELINE.MIN_LOOP_DURATION);
                this.updateLoop(loop.id, { start: Math.max(0, newStart) });
            } else {
                const newEnd = Math.max(this.currentTime, loop.start + TIMELINE.MIN_LOOP_DURATION);
                this.updateLoop(loop.id, { end: Math.min(this.duration, newEnd) });
            }
            this.editState = { ...this.editState, boundary };
        }

        previewLoop() {
            if (!this.editingLoop) return;
            this.startCountdownThenPlay(this.editingLoop.start);
        }

        previewEnd() {
            if (!this.editingLoop || !this.isMediaReady()) return;
            const previewStart = Math.max(this.editingLoop.start, this.editingLoop.end - TIMING.PREVIEW_BEFORE_END_S);
            this.seekTo(previewStart);
            this.play();
        }

        // ─── Timeline Zoom ─────────────────────────────────────────────────────

        private _calculateZoomForLoop(loop: Loop): { start: number; end: number } {
            const loopDuration = loop.end - loop.start;
            const padding = loopDuration * TIMELINE.ZOOM_PADDING;

            let visibleDuration = loopDuration + padding * 2;

            if (visibleDuration < TIMELINE.MIN_VISIBLE_DURATION) {
                const extraPadding = (TIMELINE.MIN_VISIBLE_DURATION - visibleDuration) / 2;
                const start = Math.max(0, loop.start - padding - extraPadding);
                const end = Math.min(this.duration, loop.end + padding + extraPadding);
                return { start, end };
            }

            const start = Math.max(0, loop.start - padding);
            const end = Math.min(this.duration, loop.end + padding);
            return { start, end };
        }

        getTimelineZoom(): { start: number; end: number; isZoomed: boolean } {
            // When dragging, use locked zoom to prevent handles from jumping
            if (this.draggingBoundary && this._lockedZoom) {
                return { ...this._lockedZoom, isZoomed: true };
            }
            // Use zoom when explicitly enabled and we have an active loop
            if (this.zoomToLoop && this.activeLoop) {
                const zoomRange = this._calculateZoomForLoop(this.activeLoop);
                return { ...zoomRange, isZoomed: true };
            }
            return { start: 0, end: this.duration, isZoomed: false };
        }

        /** Toggle zoom to loop */
        toggleZoom() {
            this.zoomToLoop = !this.zoomToLoop;
        }

        handleTimelineClick(e: MouseEvent) {
            const target = e.currentTarget as HTMLElement;
            const rect = target.getBoundingClientRect();
            const percent = (e.clientX - rect.left) / rect.width;

            const zoom = this.getTimelineZoom();
            const visibleDuration = zoom.end - zoom.start;
            const clickTime = zoom.start + percent * visibleDuration;
            this.seekTo(clickTime);
        }

        // ─── Drag Handlers ─────────────────────────────────────────────────────

        startDrag(boundary: 'start' | 'end', loopId: string, e: MouseEvent | TouchEvent) {
            e.preventDefault();
            e.stopPropagation();
            this.draggingBoundary = boundary;
            this.editState = { loopId, boundary };

            // Lock the current zoom to prevent handles from jumping during drag
            if (this.zoomToLoop && this.activeLoop) {
                this._lockedZoom = this._calculateZoomForLoop(this.activeLoop);
            } else {
                this._lockedZoom = { start: 0, end: this.duration };
            }

            this._timelineRef = this.shadowRoot?.querySelector('.lp-timeline-handles') as HTMLElement;

            // Store initial click position and boundary time for offset-based dragging
            const loop = this.loops.find(l => l.id === loopId);
            if (loop) {
                this._dragStartTime = boundary === 'start' ? loop.start : loop.end;
                if (e instanceof MouseEvent) {
                    this._dragStartX = e.clientX;
                } else if (e.touches.length > 0) {
                    this._dragStartX = e.touches[0].clientX;
                }
            }

            window.addEventListener('mousemove', this._boundDragMove);
            window.addEventListener('mouseup', this._boundDragEnd);
            window.addEventListener('touchmove', this._boundTouchMove, { passive: false });
            window.addEventListener('touchend', this._boundDragEnd);
        }

        private _boundDragMove = (e: MouseEvent) => {
            if (!this.draggingBoundary || !this._timelineRef || !this.editingLoop) return;
            if (this._dragStartX === null || this._dragStartTime === null) return;

            const rect = this._timelineRef.getBoundingClientRect();
            const zoom = this.getTimelineZoom();
            const visibleDuration = zoom.end - zoom.start;

            // Calculate delta from initial click position
            const deltaX = e.clientX - this._dragStartX;
            const deltaPercent = deltaX / rect.width;
            const deltaTime = deltaPercent * visibleDuration;

            // Apply delta to original boundary time
            const newTime = this._dragStartTime + deltaTime;
            this._updateBoundaryFromDrag(newTime);
        };

        private _boundTouchMove = (e: TouchEvent) => {
            if (!this.draggingBoundary || !this._timelineRef || !this.editingLoop) return;
            if (this._dragStartX === null || this._dragStartTime === null) return;
            e.preventDefault();

            const touch = e.touches[0];
            const rect = this._timelineRef.getBoundingClientRect();
            const zoom = this.getTimelineZoom();
            const visibleDuration = zoom.end - zoom.start;

            // Calculate delta from initial touch position
            const deltaX = touch.clientX - this._dragStartX;
            const deltaPercent = deltaX / rect.width;
            const deltaTime = deltaPercent * visibleDuration;

            // Apply delta to original boundary time
            const newTime = this._dragStartTime + deltaTime;
            this._updateBoundaryFromDrag(newTime);
        };

        private _updateBoundaryFromDrag(newTime: number) {
            if (!this.draggingBoundary || !this.editingLoop) return;

            const loop = this.editingLoop;

            if (this.draggingBoundary === 'start') {
                const clampedStart = Math.max(0, Math.min(loop.end - TIMELINE.MIN_LOOP_DURATION, newTime));
                this.updateLoop(loop.id, { start: clampedStart });
            } else {
                const clampedEnd = Math.min(this.duration, Math.max(loop.start + TIMELINE.MIN_LOOP_DURATION, newTime));
                this.updateLoop(loop.id, { end: clampedEnd });
            }
        }

        private _boundDragEnd = () => {
            this.draggingBoundary = null;
            this._timelineRef = null;
            this._lockedZoom = null;  // Unlock zoom
            this._dragStartX = null;  // Reset drag offset tracking
            this._dragStartTime = null;
            this._cleanupDragListeners();
        };

        private _cleanupDragListeners() {
            window.removeEventListener('mousemove', this._boundDragMove);
            window.removeEventListener('mouseup', this._boundDragEnd);
            window.removeEventListener('touchmove', this._boundTouchMove);
            window.removeEventListener('touchend', this._boundDragEnd);
        }

        // ─── Keyboard Handler ──────────────────────────────────────────────────

        private _boundKeyboardHandler = (e: KeyboardEvent) => {
            this.handleKeyboard(e);
        };

        handleKeyboard(e: KeyboardEvent) {
            // Don't handle shortcuts when editing loop name or time
            if (this._editingLoopName || this._editingStartTime || this._editingEndTime) {
                return;
            }

            // Check if focus is on an input element
            const target = e.target as Element;
            if (
                target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement ||
                target instanceof HTMLSelectElement ||
                target.getAttribute('contenteditable') === 'true'
            ) {
                return;
            }

            // All keyboard shortcuts
            switch (e.key) {
                case ' ':
                    e.preventDefault();
                    this.togglePlayPause();
                    break;

                case 'ArrowLeft':
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.shiftKey && this.activeLoopId) {
                        // Shift+Left: adjust active boundary when loop selected
                        this.adjustEditingBoundary(-ADJUSTMENTS.LOOP_ADJUST_FINE_S);
                    } else {
                        // Left: always seek
                        this.seek(e.shiftKey ? -ADJUSTMENTS.SEEK_FINE_S : -ADJUSTMENTS.SEEK_NORMAL_S);
                    }
                    break;

                case 'ArrowRight':
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.shiftKey && this.activeLoopId) {
                        // Shift+Right: adjust active boundary when loop selected
                        this.adjustEditingBoundary(ADJUSTMENTS.LOOP_ADJUST_FINE_S);
                    } else {
                        // Right: always seek
                        this.seek(e.shiftKey ? ADJUSTMENTS.SEEK_FINE_S : ADJUSTMENTS.SEEK_NORMAL_S);
                    }
                    break;

                case 'ArrowUp':
                    e.preventDefault();
                    e.stopPropagation();
                    this.adjustSpeed(ADJUSTMENTS.SPEED_STEP);
                    break;

                case 'ArrowDown':
                    e.preventDefault();
                    e.stopPropagation();
                    this.adjustSpeed(-ADJUSTMENTS.SPEED_STEP);
                    break;

                case 'Tab':
                    // Tab: switch active boundary when loop selected
                    if (this.activeLoopId) {
                        e.preventDefault();
                        this.switchEditBoundary();
                    }
                    break;

                case 'Escape':
                    e.preventDefault();
                    // Escape turns off loop mode entirely
                    if (this.loopEnabled) {
                        this.loopEnabled = false;
                        this.activeLoopId = null;
                        this.exitEditMode();
                        this.saveCurrentState();
                    }
                    break;

                case 'l':
                case 'L':
                    e.preventDefault();
                    this.toggleLoopEnabled();
                    break;

                case 'c':
                case 'C':
                    e.preventDefault();
                    this.toggleCountdownEnabled();
                    break;

                case 'z':
                case 'Z':
                    e.preventDefault();
                    if (this.loopEnabled && this.activeLoop) {
                        this.toggleZoom();
                    }
                    break;

                case 'r':
                case 'R':
                    e.preventDefault();
                    this.returnToLoopStart();
                    break;

                case 's':
                case 'S':
                    e.preventDefault();
                    this._setLoopStart();
                    break;

                case 'e':
                case 'E':
                    e.preventDefault();
                    this._setLoopEnd();
                    break;

                case 'n':
                case 'N':
                    e.preventDefault();
                    this._addNewLoop();
                    break;

                case 'Delete':
                case 'Backspace':
                    if (this.activeLoopId) {
                        e.preventDefault();
                        this.deleteLoop(this.activeLoopId);
                    }
                    break;

                case '0':
                    e.preventDefault();
                    this.activateLoop(null);
                    break;

                case '1':
                case '2':
                case '3':
                case '4':
                case '5':
                case '6':
                case '7':
                case '8':
                case '9':
                    e.preventDefault();
                    this.selectLoopByIndex(parseInt(e.key));
                    break;
            }
        }

        // ─── State Persistence ─────────────────────────────────────────────────

        private _getPlayerState(): PlayerState {
            return {
                loops: this.loops,
                activeLoopId: this.activeLoopId,
                loopEnabled: this.loopEnabled,
                expanded: this.expanded,
                playbackSpeed: this.playbackSpeed,
                pitch: (this as any).pitch, // Access pitch if it exists (audio-loop-player only)
                countdownEnabled: this.countdownEnabled,
                lastPosition: this.currentTime,
            };
        }

        saveCurrentState() {
            if (this._debouncedSave) {
                this._debouncedSave(this._getPlayerState());
            }
        }

        restoreState() {
            const saved = loadState<PlayerState>(this.getMediaId(), this.storagePrefix);
            if (saved) {
                this.loops = saved.loops;
                this.activeLoopId = saved.activeLoopId;
                this.loopEnabled = saved.loopEnabled;
                this.expanded = saved.expanded ?? false;
                this.playbackSpeed = saved.playbackSpeed;
                this.countdownEnabled = saved.countdownEnabled ?? true;

                // Restore pitch if it exists (audio-loop-player only)
                if (saved.pitch !== undefined && 'pitch' in this) {
                    (this as any).pitch = saved.pitch;
                }

                // Sync editState when restoring an active loop
                if (saved.activeLoopId && saved.loopEnabled) {
                    this.editState = { loopId: saved.activeLoopId, boundary: 'end' };
                }

                if (this.isMediaReady() && saved.playbackSpeed !== 1) {
                    this.setPlaybackRate(saved.playbackSpeed);
                }
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return LoopPlayerMixinClass as unknown as Constructor<LoopPlayerMixinInterface> & T;
}
